import { LocaleTranslationObject } from '@shared/types/translation.type';

import { DecodeOptions } from '../codec';
import {
  FILTER_OP_SHAPES,
  FieldPath,
  FilterLiteral,
  FilterOpName,
  FilterPredicate,
  LabelLiteral,
  LabelLiterals,
  LabelRange,
  LiteralKind,
  LiteralRange,
  Quantifier,
  fieldPath,
  literalKind,
  opArgument,
  opName,
  pathKey
} from '../wire';

export type FieldType =
  | 'UID'
  | 'UID_LIST'
  | 'NUMBER'
  | 'INTEGER'
  | 'BOOL'
  | 'TEXT'
  | 'MASS'
  | 'DURATION'
  | 'LENGTH'
  | 'DATE'
  | 'DATE_TIME'
  | 'TAGS'
  | 'LABELS'
  | 'LIST';

export interface FilterField {
  key: string;
  path: FieldPath;
  label: Partial<LocaleTranslationObject> | null;
  type: FieldType;
  operators: FilterOpName[];
  /** Literal kinds each literal-bearing operator accepts, most specific editor first. */
  literalKinds: Partial<Record<FilterOpName, readonly LiteralKind[]>>;
  uidEntityType?: string;
  enumValues?: string[];
  /** The served title of a closed value, by the raw value the wire carries. */
  enumLabels?: Record<string, Partial<LocaleTranslationObject>>;
  /** Compatibility only: the first segment is a collection whose quantifier the condition chooses. */
  collection?: boolean;
  /**
   * Set on a field whose name also takes a key, `name[key]`: the uid entity type the key is picked
   * from, and what applies once a key is chosen. The field's own operators are those of the bare
   * name, and may be none.
   */
  keyed?: {
    by: string;
    operators: FilterOpName[];
    literalKinds: Partial<Record<FilterOpName, readonly LiteralKind[]>>;
  };
}

export interface FilterAssociation {
  edge: string;
  label: LocaleTranslationObject | null;
  quantifiers: Quantifier[];
  cardinality: 'one' | 'many';
  targetEntityType: string | null;
  sortableLeaves: string[];
}

export interface SortableField {
  field: string;
  label: Partial<LocaleTranslationObject> | null;
  association?: { edge: string; cardinality: 'one' | 'many' };
}

export interface FilterVocabulary {
  readonly entityType: string;
  readonly allowsSelfRef: boolean;
  readonly allowsRelativeDate: boolean;
  /** `edge` null for the root entity, an edge path (see `edgePath`) for an association's target. */
  fields(edge: string | null): readonly FilterField[];
  /** A keyed path, `name[key]`, resolves to the field of `name` with the operators of a key. */
  field(edge: string | null, path: FieldPath): FilterField | undefined;
  /** The associations a filter written in `within` (an edge path, null for the root) may hold. */
  associations(within?: string | null): readonly FilterAssociation[];
  association(path: string): FilterAssociation | undefined;
  selfFields(): readonly FilterField[];
  sortable(): readonly SortableField[];
}

export interface VocabularyData {
  entityType: string;
  allowsSelfRef: boolean;
  allowsRelativeDate: boolean;
  root: FilterField[];
  /** Fields of each association's target, keyed by edge path. */
  scoped: ReadonlyMap<string, FilterField[]>;
  associations: FilterAssociation[];
  /** Associations nested in an association's filter, keyed by the outer edge path. */
  nested?: ReadonlyMap<string, FilterAssociation[]>;
  selfFields: FilterField[];
  sortable: SortableField[];
}

export function keyedName(base: string, key: string): string {
  return `${base}[${key}]`;
}

export function splitKeyedName(name: string): { base: string; key: string } | null {
  const match = /^(.+)\[([A-Za-z0-9_]+)\]$/.exec(name);
  return match ? { base: match[1], key: match[2] } : null;
}

export class StaticVocabulary implements FilterVocabulary {
  constructor(private readonly data: VocabularyData) {}

  get entityType(): string {
    return this.data.entityType;
  }

  get allowsSelfRef(): boolean {
    return this.data.allowsSelfRef;
  }

  get allowsRelativeDate(): boolean {
    return this.data.allowsRelativeDate;
  }

  fields(edge: string | null): readonly FilterField[] {
    return edge === null ? this.data.root : (this.data.scoped.get(edge) ?? []);
  }

  field(edge: string | null, path: FieldPath): FilterField | undefined {
    const fields = this.fields(edge);
    const key = pathKey(path);
    const exact = fields.find((field) => field.key === key);
    const [segment] = path.segments;
    if (exact || path.segments.length !== 1 || !('field' in segment)) return exact;
    const split = splitKeyedName(segment.field.name);
    if (!split) return undefined;
    const baseKey = pathKey(fieldPath(split.base));
    const base = fields.find((field) => field.key === baseKey);
    return base?.keyed
      ? { ...base, operators: base.keyed.operators, literalKinds: base.keyed.literalKinds }
      : undefined;
  }

  associations(within: string | null = null): readonly FilterAssociation[] {
    return within === null ? this.data.associations : (this.data.nested?.get(within) ?? []);
  }

  association(path: string): FilterAssociation | undefined {
    const cut = path.lastIndexOf('/');
    const within = cut === -1 ? null : path.slice(0, cut);
    const edge = path.slice(cut + 1);
    return this.associations(within).find((association) => association.edge === edge);
  }

  selfFields(): readonly FilterField[] {
    return this.data.selfFields;
  }

  sortable(): readonly SortableField[] {
    return this.data.sortable;
  }
}

function argumentLiterals(predicate: FilterPredicate): FilterLiteral[] {
  const name = opName(predicate.op);
  const arg = opArgument(predicate.op);
  switch (FILTER_OP_SHAPES[name]) {
    case 'literal':
      return [arg as FilterLiteral];
    case 'range':
      return [(arg as LiteralRange).lo, (arg as LiteralRange).hi];
    case 'literals':
      return arg as FilterLiteral[];
    case 'label_literal':
      return [(arg as LabelLiteral).value];
    case 'label_literals':
      return (arg as LabelLiterals).values;
    case 'label_range':
      return [(arg as LabelRange).lo, (arg as LabelRange).hi];
    default:
      return [];
  }
}

/**
 * The editor for a value is chosen from the field's literal kinds, so a literal of any other kind
 * could not be shown or kept as written. `null` (an open bound, an empty value) is always accepted;
 * `self` wherever the vocabulary allows self-references.
 */
function acceptsLiterals(
  vocabulary: FilterVocabulary,
  field: FilterField,
  predicate: FilterPredicate
): boolean {
  const kinds = field.literalKinds[opName(predicate.op)] ?? [];
  return argumentLiterals(predicate).every((literal) => {
    const kind = literalKind(literal);
    return (
      kind === 'null' ||
      (kind === 'self' && vocabulary.allowsSelfRef) ||
      kinds.includes(kind)
    );
  });
}

/** Codec options that keep everything this vocabulary cannot edit as opaque nodes. */
export function decodeOptionsFor(vocabulary: FilterVocabulary): DecodeOptions {
  return {
    acceptPredicate: (predicate, edge) => {
      const field = vocabulary.field(edge, predicate.path);
      return (
        !!field &&
        field.operators.includes(opName(predicate.op)) &&
        acceptsLiterals(vocabulary, field, predicate)
      );
    },
    acceptAssociation: (path) => vocabulary.association(path) !== undefined
  };
}
